import React, { useState, useEffect, useRef } from 'react';
import { auth, provider, db } from './firebase';
import { signInWithPopup, signOut, onAuthStateChanged } from 'firebase/auth';
import { collection, addDoc, updateDoc, deleteDoc, doc, onSnapshot, query, getCountFromServer } from 'firebase/firestore';
import { advance, getDueCards } from './sm2';
import './App.css';

const ALLOWED_EMAIL = 'irina.boldea@gmail.com';
const DECK_COLORS = ['#4F46E5', '#0EA5E9', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

const GREETINGS = [
  "What a great day to learn something new! 🌟",
  "Every word you learn is a door to a new world. 🚪",
  "Keep going — your future self will thank you. 💪",
  "Small steps every day lead to big progress. 🌱",
  "Language learning is a superpower. ⚡",
  "You're doing great — one card at a time! 🃏",
  "Consistency beats intensity. Show up today! 📅",
  "The best time to review is right now. ✨",
  "A new word a day keeps the forgetting away. 🧠",
  "You've got this. Let's make today count! 🎯",
];

const getDailyGreeting = () => {
  const day = Math.floor(Date.now() / 86400000);
  return GREETINGS[day % GREETINGS.length];
};

export default function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('decks');
  const [decks, setDecks] = useState([]);
  const [cards, setCards] = useState([]);
  const [activeDeck, setActiveDeck] = useState(null);
  const [studyQueue, setStudyQueue] = useState([]);
  const [studyIndex, setStudyIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [newDeckName, setNewDeckName] = useState('');
  const [newFront, setNewFront] = useState('');
  const [newBack, setNewBack] = useState('');
  const [newExamples, setNewExamples] = useState('');
  const [newSynonyms, setNewSynonyms] = useState('');
  const [selectedCard, setSelectedCard] = useState(null);
  const [editingCard, setEditingCard] = useState(false);
  const [editFront, setEditFront] = useState('');
  const [editBack, setEditBack] = useState('');
  const [editExamples, setEditExamples] = useState('');
  const [editSynonyms, setEditSynonyms] = useState('');
  const [editingDeckName, setEditingDeckName] = useState(false);
  const [draftDeckName, setDraftDeckName] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [importStatus, setImportStatus] = useState('');
  const [authError, setAuthError] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    return onAuthStateChanged(auth, async u => {
      if (u && u.email !== ALLOWED_EMAIL) {
        await signOut(auth);
        setAuthError('Access restricted. This app is private.');
        setLoading(false);
        return;
      }
      setUser(u);
      setLoading(false);
    });
  }, []);

  // Seed one history entry so the first back press can be intercepted
  useEffect(() => { history.pushState(null, ''); }, []);

  useEffect(() => {
    const handlePopState = () => {
      if (view === 'study') {
        history.pushState(null, '');
        setView('edit'); setFlipped(false);
      } else if (view === 'viewCard') {
        history.pushState(null, '');
        setView('edit'); setSelectedCard(null); setEditingCard(false);
      } else if (view === 'edit') {
        history.pushState(null, '');
        setView('decks'); setActiveDeck(null);
      }
      // 'decks': don't re-push — next back press closes / minimises the app
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [view]);

  useEffect(() => {
    if (!user) return;
    const q = query(collection(db, 'users', user.uid, 'decks'));
    return onSnapshot(q, async snap => {
      const deckList = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      const counts = await Promise.all(
        deckList.map(d => getCountFromServer(collection(db, 'users', user.uid, 'decks', d.id, 'cards')))
      );
      setDecks(deckList.map((d, i) => ({ ...d, cardCount: counts[i].data().count })));
    });
  }, [user]);

  useEffect(() => {
    if (!user || !activeDeck) return;
    const q = query(collection(db, 'users', user.uid, 'decks', activeDeck.id, 'cards'));
    return onSnapshot(q, snap => setCards(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
  }, [user, activeDeck]);

  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const weekday = d.toLocaleDateString('en-US', { weekday: 'long' });
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${weekday} ${day}.${month}.${year}`;
  };

  const login = async () => {
    setAuthError('');
    const result = await signInWithPopup(auth, provider);
    if (result.user.email !== ALLOWED_EMAIL) {
      await signOut(auth);
      setAuthError('Access restricted. This app is private.');
    }
  };
  const logout = () => { signOut(auth); setUser(null); setActiveDeck(null); setView('decks'); };

  const addDeck = async () => {
    if (!newDeckName.trim()) return;
    await addDoc(collection(db, 'users', user.uid, 'decks'), { name: newDeckName.trim(), createdAt: new Date().toISOString() });
    setNewDeckName('');
  };

  const deleteDeck = async (deckId) => {
    if (!window.confirm('Delete this deck and all its cards?')) return;
    await deleteDoc(doc(db, 'users', user.uid, 'decks', deckId));
  };

  const saveDeckName = async () => {
    const name = draftDeckName.trim();
    if (name && name !== activeDeck.name) {
      await updateDoc(doc(db, 'users', user.uid, 'decks', activeDeck.id), { name });
      setActiveDeck(d => ({ ...d, name }));
    }
    setEditingDeckName(false);
  };

  const addCard = async () => {
    if (!newFront.trim() || !newBack.trim()) return;
    await addDoc(collection(db, 'users', user.uid, 'decks', activeDeck.id, 'cards'), {
      front: newFront.trim(), back: newBack.trim(),
      examples: newExamples.trim(),
      synonyms: newSynonyms.trim(),
      createdAt: new Date().toISOString(),
      step: 0, nextReview: null
    });
    await updateDoc(doc(db, 'users', user.uid, 'decks', activeDeck.id), { lastCardAt: new Date().toISOString() });
    setNewFront(''); setNewBack(''); setNewExamples(''); setNewSynonyms('');
  };

  const deleteCard = async (cardId) => {
    await deleteDoc(doc(db, 'users', user.uid, 'decks', activeDeck.id, 'cards', cardId));
  };

  const saveCardEdit = async () => {
    if (!editFront.trim() || !editBack.trim()) return;
    const updated = { front: editFront.trim(), back: editBack.trim(), examples: editExamples.trim(), synonyms: editSynonyms.trim() };
    await updateDoc(doc(db, 'users', user.uid, 'decks', activeDeck.id, 'cards', selectedCard.id), updated);
    setSelectedCard(c => ({ ...c, ...updated }));
    setEditingCard(false);
  };

  const importFromJson = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      if (!Array.isArray(data)) throw new Error('JSON must be an array of cards.');
      const valid = data.filter(c => c.front?.trim() && c.back?.trim());
      if (valid.length === 0) throw new Error('No valid cards found. Each card needs "front" and "back".');
      setImportStatus('Importing…');
      await Promise.all(valid.map(c => addDoc(
        collection(db, 'users', user.uid, 'decks', activeDeck.id, 'cards'),
        {
          front: c.front.trim(),
          back: c.back.trim(),
          examples: c.examples?.trim() ?? '',
          synonyms: c.synonyms?.trim() ?? '',
          createdAt: new Date().toISOString(),
          step: 0, nextReview: null,
        }
      )));
      await updateDoc(doc(db, 'users', user.uid, 'decks', activeDeck.id), { lastCardAt: new Date().toISOString() });
      setImportStatus(`✓ Imported ${valid.length} card${valid.length !== 1 ? 's' : ''}`);
    } catch (err) {
      setImportStatus(`Error: ${err.message}`);
    }
    setTimeout(() => setImportStatus(''), 4000);
  };

  const startStudy = () => {
    const due = getDueCards(cards);
    if (due.length === 0) return;
    setStudyQueue([...due].sort(() => Math.random() - 0.5));
    setStudyIndex(0);
    setFlipped(false);
    setView('study');
  };

  const rateCard = async (remembered) => {
    const card = studyQueue[studyIndex];
    const result = advance(card, remembered);
    await updateDoc(doc(db, 'users', user.uid, 'decks', activeDeck.id, 'cards', card.id), result);
    if (studyIndex + 1 >= studyQueue.length) {
      setView('edit');
    } else {
      setStudyIndex(i => i + 1);
      setFlipped(false);
    }
  };

  if (loading) return <div className="center"><div className="spinner" /></div>;

  if (!user) return (
    <div className="login-screen">
      <div className="login-hero">
        <div className="app-logo">🃏</div>
        <h1>Flashcards</h1>
        <p>Spaced repetition for your phone and browser</p>
      </div>
      <div className="login-body">
        <h2>Sign in to continue</h2>
        <button className="btn-google" onClick={login}>
          <svg width="20" height="20" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.2l6.7-6.7C35.8 2.5 30.2 0 24 0 14.7 0 6.7 5.4 2.7 13.3l7.8 6c1.8-5.4 6.9-9.8 13.5-9.8z"/><path fill="#4285F4" d="M46.6 24.5c0-1.6-.1-3.1-.4-4.5H24v8.5h12.7c-.6 3-2.3 5.5-4.8 7.2l7.5 5.8c4.4-4 6.9-10 6.9-17z"/><path fill="#FBBC05" d="M10.5 28.6A14.9 14.9 0 0 1 9.5 24c0-1.6.3-3.2.8-4.6l-7.8-6A23.9 23.9 0 0 0 0 24c0 3.9.9 7.5 2.7 10.7l7.8-6.1z"/><path fill="#34A853" d="M24 48c6.2 0 11.4-2 15.2-5.5l-7.5-5.8c-2 1.4-4.6 2.2-7.7 2.2-6.6 0-12.1-4.4-14.1-10.4l-7.8 6.1C6.7 42.6 14.7 48 24 48z"/></svg>
          Continue with Google
        </button>
        {authError && <p className="auth-error">{authError}</p>}
      </div>
    </div>
  );

  if (view === 'study') {
    const card = studyQueue[studyIndex];
    if (!card) return <div className="center"><p>Loading...</p></div>;
    return (
      <div className="study-screen">
        <header className="top-bar study-top-bar">
          <button className="icon-btn" onClick={() => setView('edit')}>← Back</button>
          <span className="progress-text">{studyIndex + 1} / {studyQueue.length}</span>
        </header>
        <div className="study-progress-bar">
          <div className="study-progress-fill" style={{ width: `${((studyIndex + 1) / studyQueue.length) * 100}%` }} />
        </div>
        <div className="study-area">
          <div className={`flashcard ${flipped ? 'flipped' : ''}`} onClick={() => setFlipped(f => !f)}>
            <div className="card-inner">
              <div className="card-front">
                <span className="card-label">Question</span>
                <p>{card.front}</p>
              </div>
              <div className="card-back">
                <span className="card-label">Answer</span>
                <p>{card.back}</p>
              </div>
            </div>
          </div>
          {!flipped && <p className="tap-hint">Tap to reveal answer</p>}
          {flipped && (
            <>
              <div className="rating-row">
                <button className="rate-btn rate-0" onClick={() => rateCard(false)}>😵<br/>Forgot</button>
                <button className="rate-btn rate-5" onClick={() => rateCard(true)}>✓<br/>Got it</button>
              </div>
              {card.examples && (
                <div className="study-examples">
                  <span className="card-label">Examples</span>
                  <p>{card.examples}</p>
                </div>
              )}
              {card.synonyms && (
                <div className="study-examples">
                  <span className="card-label">Synonyms</span>
                  <p>{card.synonyms}</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  if (view === 'viewCard' && selectedCard) {
    return (
      <div className="screen screen--wide">
        <header className="top-bar">
          <button className="icon-btn" onClick={() => { setView('edit'); setSelectedCard(null); setEditingCard(false); }}>← Back</button>
          <h2>{editingCard ? 'Edit card' : selectedCard.front}</h2>
          {editingCard
            ? <button className="btn-primary" onClick={saveCardEdit}>Save</button>
            : <button className="icon-btn" onClick={() => { setEditFront(selectedCard.front); setEditBack(selectedCard.back); setEditExamples(selectedCard.examples || ''); setEditSynonyms(selectedCard.synonyms || ''); setEditingCard(true); }}>Edit</button>
          }
        </header>
        <div className="card-detail">
          {editingCard ? (
            <>
              <div className="card-detail-section">
                <span className="card-detail-label">Front</span>
                <textarea className="card-edit-input" value={editFront} onChange={e => setEditFront(e.target.value)} rows={2} />
              </div>
              <div className="card-detail-section">
                <span className="card-detail-label">Back</span>
                <textarea className="card-edit-input" value={editBack} onChange={e => setEditBack(e.target.value)} rows={2} />
              </div>
              <div className="card-detail-section">
                <span className="card-detail-label">Examples</span>
                <textarea className="card-edit-input" value={editExamples} onChange={e => setEditExamples(e.target.value)} rows={4} placeholder="Optional" />
              </div>
              <div className="card-detail-section">
                <span className="card-detail-label">Synonyms</span>
                <textarea className="card-edit-input" value={editSynonyms} onChange={e => setEditSynonyms(e.target.value)} rows={2} placeholder="Optional" />
              </div>
            </>
          ) : (
            <>
              <div className="card-detail-section">
                <span className="card-detail-label">Front</span>
                <p className="card-detail-text">{selectedCard.front}</p>
              </div>
              <div className="card-detail-section">
                <span className="card-detail-label">Back</span>
                <p className="card-detail-text answer">{selectedCard.back}</p>
              </div>
              {selectedCard.examples && (
                <div className="card-detail-section">
                  <span className="card-detail-label">Examples</span>
                  <p className="card-detail-text examples">{selectedCard.examples}</p>
                </div>
              )}
              {selectedCard.synonyms && (
                <div className="card-detail-section">
                  <span className="card-detail-label">Synonyms</span>
                  <p className="card-detail-text synonyms">{selectedCard.synonyms}</p>
                </div>
              )}
              {selectedCard.createdAt && (
                <p className="card-added-date">Added {formatDate(selectedCard.createdAt)}</p>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  if (view === 'edit' && activeDeck) {
    const dueCount = getDueCards(cards).length;
    return (
      <div className="screen screen--wide">
        <header className="top-bar">
          <button className="icon-btn" onClick={() => { setView('decks'); setActiveDeck(null); }}>← Decks</button>
          {editingDeckName
            ? <input
                className="deck-name-input"
                value={draftDeckName}
                onChange={e => setDraftDeckName(e.target.value)}
                onBlur={saveDeckName}
                onKeyDown={e => { if (e.key === 'Enter') saveDeckName(); if (e.key === 'Escape') setEditingDeckName(false); }}
                autoFocus
              />
            : <h2 className="deck-name-editable" onClick={() => { setDraftDeckName(activeDeck.name); setEditingDeckName(true); }}>{activeDeck.name}</h2>
          }
          <button className="btn-primary" onClick={startStudy} disabled={dueCount === 0}>
            {dueCount > 0 ? `Study (${dueCount})` : 'All done ✓'}
          </button>
        </header>
        <div className="edit-layout">
          <div className="card-list">
            {cards.length === 0 && <p className="empty-hint">No cards yet. Add your first card below.</p>}
            {cards.map(card => (
              <div key={card.id} className="card-row" onClick={() => { setSelectedCard(card); setView('viewCard'); }}>
                <div className="card-texts">
                  <span className="card-front-text">{card.front}</span>
                  <span className="card-back-text">{card.back}</span>
                  {card.createdAt && <span className="card-date-text">{formatDate(card.createdAt)}</span>}
                </div>
                <button className="icon-btn danger" onClick={e => { e.stopPropagation(); deleteCard(card.id); }}>✕</button>
              </div>
            ))}
          </div>
          <button className="fab" onClick={() => setShowAddForm(true)} aria-label="Add card">＋</button>
          <div className={`add-card-form${showAddForm ? ' add-card-form--open' : ''}`}>
            <div className="add-card-form-header">
              <h3>Add card</h3>
              <button className="icon-btn" onClick={() => setShowAddForm(false)}>✕</button>
            </div>
            <textarea placeholder="Front (question)" value={newFront} onChange={e => setNewFront(e.target.value)} rows={2} />
            <textarea placeholder="Back (answer)" value={newBack} onChange={e => setNewBack(e.target.value)} rows={2} />
            <textarea placeholder="Examples (optional)" value={newExamples} onChange={e => setNewExamples(e.target.value)} rows={3} />
            <textarea placeholder="Synonyms (optional)" value={newSynonyms} onChange={e => setNewSynonyms(e.target.value)} rows={2} />
            <button className="btn-primary full" onClick={addCard}>Add card</button>
            <div className="import-row">
              <button className="btn-import" onClick={() => fileInputRef.current.click()}>⬆ Import JSON</button>
              {importStatus && <span className="import-status">{importStatus}</span>}
            </div>
            <input ref={fileInputRef} type="file" accept=".json,application/json" style={{ display: 'none' }} onChange={importFromJson} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="screen screen--wide">
      <header className="top-bar">
        <h1>My Decks</h1>
        <button className="icon-btn" onClick={logout}>Sign out</button>
      </header>
      <div className="daily-greeting">{getDailyGreeting()}</div>
      <div className="deck-list">
        {decks.length === 0 && <p className="empty-hint">No decks yet. Create your first one below.</p>}
        {decks.map((deck, i) => (
          <div key={deck.id} className="deck-row" style={{ '--deck-color': DECK_COLORS[i % DECK_COLORS.length] }} onClick={() => { setActiveDeck(deck); setView('edit'); }}>
            <div className="deck-color-dot" />
            <div className="deck-info">
              <span className="deck-name">{deck.name}</span>
              <span className="deck-count">{deck.cardCount ?? 0} cards</span>
              {deck.lastCardAt && <span className="deck-last-added">Last added: {formatDate(deck.lastCardAt)}</span>}
            </div>
            <button className="icon-btn danger" onClick={e => { e.stopPropagation(); deleteDeck(deck.id); }}>✕</button>
          </div>
        ))}
      </div>
      <div className="add-deck-form">
        <input placeholder="New deck name..." value={newDeckName} onChange={e => setNewDeckName(e.target.value)} onKeyDown={e => e.key === 'Enter' && addDeck()} />
        <button className="btn-primary" onClick={addDeck}>Create</button>
      </div>
    </div>
  );
}
